import { Link, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/AuthContext"
import { Clock, Zap, Crown, Menu } from "lucide-react"
import { useState } from "react"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { FuseguLogo } from "@/components/FuseguLogo"

export function Navigation() {
  const location = useLocation()
  const { user, logout } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const handleLogout = async () => {
    await logout()
  }

  const getTierBadge = () => {
    if (!user) return null

    const { subscription_tier, trial_days_remaining } = user

    if (subscription_tier === 'trial') {
      return (
        <Link to="/billing">
          <div className="flex items-center gap-1.5 px-2 lg:px-3 py-1.5 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors cursor-pointer">
            <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
              Trial {trial_days_remaining}d
            </span>
          </div>
        </Link>
      )
    }

    if (subscription_tier === 'pro') {
      return (
        <Link to="/billing">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors cursor-pointer">
            <Crown className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              Pro
            </span>
          </div>
        </Link>
      )
    }

    // None tier - show nothing in nav, user can access billing page from sidebar
    return null
  }

  const navLinks = (
    <>
      <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
        <Button variant="ghost" className={`w-full justify-start ${isActive('/dashboard') ? "text-foreground" : "text-muted-foreground"}`}>
          Dashboard
        </Button>
      </Link>
      <Link to="/scans" onClick={() => setMobileMenuOpen(false)}>
        <Button variant="ghost" className={`w-full justify-start ${isActive('/scans') ? "text-foreground" : "text-muted-foreground"}`}>
          Scans
        </Button>
      </Link>
      <Link to="/reports" onClick={() => setMobileMenuOpen(false)}>
        <Button variant="ghost" className={`w-full justify-start ${isActive('/reports') ? "text-foreground" : "text-muted-foreground"}`}>
          Reports
        </Button>
      </Link>
      <Link to="/branding" onClick={() => setMobileMenuOpen(false)}>
        <Button variant="ghost" className={`w-full justify-start ${isActive('/branding') ? "text-foreground" : "text-muted-foreground"}`}>
          Branding
        </Button>
      </Link>
      <Link to="/billing" onClick={() => setMobileMenuOpen(false)}>
        <Button variant="ghost" className={`w-full justify-start ${isActive('/billing') ? "text-foreground" : "text-muted-foreground"}`}>
          Billing
        </Button>
      </Link>
    </>
  )

  return (
    <header className="fixed top-0 left-0 right-0 flex h-16 items-center gap-2 md:gap-4 border-b bg-background px-4 md:px-6 z-50 overflow-x-hidden">
      {/* Logo */}
      <Link to="/dashboard" className="flex items-center gap-2 font-bold text-lg flex-shrink-0">
        <FuseguLogo className="h-6 w-6 flex-shrink-0" />
        <span>Fusegu</span>
      </Link>

      {/* Desktop Navigation */}
      <nav className="hidden md:flex md:flex-row md:items-center md:gap-1 lg:gap-2 flex-shrink min-w-0">
        {navLinks}
      </nav>

      {/* Right side - Desktop */}
      <div className="ml-auto hidden md:flex items-center gap-2 lg:gap-4 flex-shrink-0">
        {getTierBadge()}
        {user && (
          <span className="hidden lg:inline text-sm text-muted-foreground">
            {user.username}
          </span>
        )}
        <Button variant="outline" size="sm" onClick={handleLogout}>
          Logout
        </Button>
      </div>

      {/* Mobile Menu Button & Right Side */}
      <div className="ml-auto flex md:hidden items-center gap-2">
        {/* Mobile Tier Badge (compact) */}
        {user && user.subscription_tier === 'trial' && (
          <Link to="/upgrade">
            <Button size="sm" variant="ghost" className="px-2" aria-label="Upgrade to Pro">
              <Zap className="h-4 w-4" />
            </Button>
          </Link>
        )}

        {/* Mobile Menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" className="px-2">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-64">
            <div className="flex flex-col gap-4 mt-8">
              {/* User info */}
              {user && (
                <div className="pb-4 border-b">
                  <p className="text-sm font-medium">{user.username}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <div className="mt-3">
                    {getTierBadge()}
                  </div>
                </div>
              )}

              {/* Navigation Links */}
              <nav className="flex flex-col gap-2">
                {navLinks}
              </nav>

              {/* Logout Button */}
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    handleLogout()
                    setMobileMenuOpen(false)
                  }}
                >
                  Logout
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}